import { Box, Grid, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContractStatus, {
  ContractActionsOptions,
  ContractPaymentForm
} from 'src/utils/constants/contracts';

import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ACTIVATE_AUTOMATIC_BILLING,
  CANCEL_CONTRACT,
  DEACTIVATE_AUTOMATIC_BILLING,
  GENERATE_AUTOMATIC_REQUEST_DOCUMENT,
  REGENERATE_AUTOMATIC_REQUEST_DOCUMENT,
  SEND_TO_DIGITAL_SIGN_CONTRACT,
  SIGN_CONTRACT_MANUALLY,
  activateAutomaticBilling,
  desactivateAutomaticBilling,
  generateAutomaticDebitRequestDocumentAction,
  regenerateAutomaticDebitRequestDocumentAction,
  sendToCancelContract,
  sendToSingDocuments,
  updateContractDates,
  createCedeContract,
  CEDE_CONTRACT,
  createEarlyTerminationContract,
  TERMINATE_CONTRACT_EARLY,
  CANCEL_CONTRACT_CESSION,
  cancelContractCessionAction,
  resendSignRequestForContractCessionAction,
  RESEND_SIGN_REQUEST_CONTRACT_CESSION,
  SEND_TO_DRAFT,
  sendToDraftAction,
  generateTerminationLetterAction,
  GENERATE_TERMINATION_LETTER,
  terminateContractAction,
  TERMINATE_CONTRACT
} from 'src/store/actions/contractActions';
import { selectTransactionBalance } from 'src/store/selectors/transactionSelectors';
import {
  CLEAR_CONTRACT_TRANSACTIONS,
  loadContractTransaction
} from 'src/store/actions/transactionActions';
import { loadClientBankAccountInformation } from 'src/store/actions/clientActions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { selectAuthRole } from 'src/store/selectors/authSelectors';
import MenuButton from 'src/components/MenuButton/MenuButton';
import AlertMessage from 'src/components/Alert/Alert';
import AlertSeverity from 'src/components/Alert/AlertEnums';
import ConfirmationDialog from 'src/components/ConfirmDialog/ConfirmationDialog';
import {
  ContractActionMenuOptions,
  getContractStatusIcon,
  isContractEligibleForCession,
  isContractEligibleForCancellation,
  selectBackGroundColorFromContractStatus,
  isContractEligibleForTermination,
  canGenerateTerminationLetter
} from 'src/utils/contract';
import useStyles from '../styles';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import EditContract from '../../contracts/EditContractDates';
import CedeContractForm from './CedeContractForm';
import EarlyTerminationContractForm from './EarlyTerminationContractForm';
import PaymentMethodSelector from './PaymentMethodSelector';

const DetailHeader = ({ contract }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const openAlert = !contract.automaticBilling;
  const role = useSelector(selectAuthRole);
  const clientBalance = useSelector(selectTransactionBalance);

  const { id } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    if (contract?.clientId) {
      dispatch(loadClientBankAccountInformation(contract.clientId));
    }
  }, [contract?.clientId, dispatch]);

  const [openCancelContract, setOpenCancelContract] = useState(false);
  const [openSendToDigitalSign, setOpenSendToDigitalSign] = useState(false);
  const [
    openGenerateAutomaticRequestDoc,
    setOpenGenerateAutomaticRequestDoc
  ] = useState(false);
  const [
    openRegenerateAutomaticRequestDoc,
    setOpenRegenerateAutomaticRequestDoc
  ] = useState(false);
  const [open, setOpen] = useState(false);
  const [openCedeContract, setOpenCedeContract] = useState(false);
  const [openCancelContractCession, setOpenCancelContractCession] = useState(
    false
  );
  const [
    openEarlyTerminationContract,
    setOpenEarlyTerminationContract
  ] = useState(false);
  const [openSendToDraft, setOpenSendToDraft] = useState(false);
  const [
    openGenerateTerminationLetter,
    setOpenGenerateTerminationLetter
  ] = useState(false);
  const [openTerminateContract, setOpenTerminateContract] = useState(false);
  const hasPendingCession =
    contract.contractCession &&
    contract.contractCession.signStatus === ContractStatus.PENDING_SIGNATURE;

  const hasPendingTermination =
    contract.contractEarlyTermination &&
    contract.contractEarlyTermination.signStatus ===
      ContractStatus.PENDING_SIGNATURE;

  const handleCloseDeleteModal = () => {
    setOpenCancelContract(false);
  };

  const handleOpenDeleteModal = () => {
    setOpenCancelContract(true);
  };

  const handleOpenSendToDigitalSign = () => {
    setOpenSendToDigitalSign(true);
  };

  const handleCloseSendToDigitalSign = () => {
    setOpenSendToDigitalSign(false);
  };

  const handleOpenGenerateAutomaticDebitRequestDocDialog = () => {
    setOpenGenerateAutomaticRequestDoc(true);
  };

  const handleOpenRegenerateAutomaticDebitRequestDocDialog = () => {
    setOpenRegenerateAutomaticRequestDoc(true);
  };

  const handleOpenCedeContractDialog = () => {
    setOpenCedeContract(true);
  };

  const handleOpenCancelContractCessionDialog = () => {
    setOpenCancelContractCession(true);
  };

  const handleOpenGenerateTerminationLetterDialog = () => {
    setOpenGenerateTerminationLetter(true);
  };

  const handleCloseCancelContractCessionDialog = () => {
    setOpenCancelContractCession(false);
  };

  const handleOpenEarlyTerminationContractDialog = () => {
    setOpenEarlyTerminationContract(true);
  };

  const handleCloseEarlyTerminationContractDialog = () => {
    setOpenEarlyTerminationContract(false);
  };

  const handleCloseCedeContractDialog = () => {
    setOpenCedeContract(false);
  };

  const handleCloseGenerateAutomaticDebitRequestDocDialog = () => {
    setOpenGenerateAutomaticRequestDoc(false);
  };

  const handleCloseRegenerateAutomaticDebitRequestDocDialog = () => {
    setOpenRegenerateAutomaticRequestDoc(false);
  };

  const handleOpenSendToDraftDialog = () => {
    setOpenSendToDraft(true);
  };

  const handleCloseSendToDraftDialog = () => {
    setOpenSendToDraft(false);
  };

  const handleCloseGenerateTerminationLetterDialog = () => {
    setOpenGenerateTerminationLetter(false);
  };

  const handleOpenTerminateContractDialog = () => {
    setOpenTerminateContract(true);
  };

  const handleCloseTerminateContractDialog = () => {
    setOpenTerminateContract(false);
  };

  useEffect(() => {
    if (openTerminateContract) {
      dispatch(loadContractTransaction(contract.id, { page: 0 }));
    }
    return () => {
      dispatch({ type: CLEAR_CONTRACT_TRANSACTIONS });
    };
  }, [openTerminateContract, dispatch, contract.id]);

  const isLoadingAction = useSelector(
    isLoadingSelector([
      SEND_TO_DIGITAL_SIGN_CONTRACT.action,
      SIGN_CONTRACT_MANUALLY.action,
      CANCEL_CONTRACT.action,
      ACTIVATE_AUTOMATIC_BILLING.action,
      DEACTIVATE_AUTOMATIC_BILLING.action,
      GENERATE_AUTOMATIC_REQUEST_DOCUMENT.action,
      REGENERATE_AUTOMATIC_REQUEST_DOCUMENT.action,
      CEDE_CONTRACT.action,
      TERMINATE_CONTRACT_EARLY.action,
      CANCEL_CONTRACT_CESSION.action,
      RESEND_SIGN_REQUEST_CONTRACT_CESSION.action,
      SEND_TO_DRAFT.action,
      GENERATE_TERMINATION_LETTER.action,
      TERMINATE_CONTRACT.action
    ])
  );

  const isLoadingTerminateContract = useSelector(
    isLoadingSelector([TERMINATE_CONTRACT.action])
  );

  const handleSendToSign = useCallback(() => {
    dispatch(sendToSingDocuments(id));
    setOpenSendToDigitalSign(false);
  }, []);

  const handleCancelContract = useCallback(() => {
    dispatch(sendToCancelContract(id));
    handleCloseDeleteModal();
  }, []);

  const handleContractAutomaticBillingActivation = useCallback(() => {
    dispatch(activateAutomaticBilling(contract.id));
  }, []);

  const handleContractAutomaticBillingDeactivation = useCallback(() => {
    dispatch(desactivateAutomaticBilling(contract.id));
  }, []);

  const handleGenerateAutomaticDebitRequestDocument = useCallback(() => {
    dispatch(generateAutomaticDebitRequestDocumentAction(contract.id));
    handleCloseGenerateAutomaticDebitRequestDocDialog();
  }, [contract]);

  const handleRegenerateAutomaticDebitRequestDocument = useCallback(() => {
    dispatch(regenerateAutomaticDebitRequestDocumentAction(contract.id));
    handleCloseRegenerateAutomaticDebitRequestDocDialog();
  }, [contract]);

  const handleCedeContract = useCallback(
    (values, resetForm) => {
      dispatch(
        createCedeContract(values, handleCloseCedeContractDialog, resetForm)
      );
    },
    [dispatch, handleCloseCedeContractDialog]
  );

  const handleCancelContractCession = useCallback(() => {
    dispatch(cancelContractCessionAction(contract.id));
    handleCloseCancelContractCessionDialog();
  }, [dispatch, handleCloseCedeContractDialog]);

  const handleEarlyTerminationContract = useCallback(
    (values, resetForm) => {
      dispatch(
        createEarlyTerminationContract(
          values,
          handleCloseEarlyTerminationContractDialog,
          resetForm
        )
      );
    },
    [dispatch, handleCloseEarlyTerminationContractDialog]
  );

  const handleResendSignRequestForContractCession = useCallback(() => {
    dispatch(resendSignRequestForContractCessionAction(contract.id));
  }, [dispatch, resendSignRequestForContractCessionAction]);

  const handleSendToDraft = useCallback(() => {
    dispatch(sendToDraftAction(id, handleCloseSendToDraftDialog));
  }, [dispatch, id, handleCloseSendToDraftDialog]);

  const handleGenerateTerminationLetter = useCallback(() => {
    dispatch(generateTerminationLetterAction(contract.id));
    handleCloseGenerateTerminationLetterDialog();
  }, [dispatch, contract]);

  const handleTerminateContract = useCallback(() => {
    dispatch(
      terminateContractAction(contract.id, handleCloseTerminateContractDialog)
    );
  }, [dispatch, contract.id, handleCloseTerminateContractDialog]);

  const selectContractActionsFromStatus = () => {
    let actions = [];

    switch (contract.status) {
      case ContractStatus.DRAFT:
        actions = ContractActionMenuOptions.filter(item =>
          [
            ContractActionsOptions.SEND_TO_DIGITAL_SIGN,
            ContractActionsOptions.EDIT_CONTRACT_DATES,
            ContractActionsOptions.CANCEL_CONTRACT
          ].includes(item.key)
        );
        break;
      case ContractStatus.PENDING_SIGNATURE:
        actions = ContractActionMenuOptions.filter(item => {
          return [
            ContractActionsOptions.CANCEL_CONTRACT,
            ContractActionsOptions.SEND_TO_DRAFT
          ].includes(item.key);
        });
        break;
      case ContractStatus.IN_PROGRESS:
        if (contract.automaticBilling) {
          actions.push(
            ContractActionMenuOptions.find(
              item =>
                item.key ===
                ContractActionsOptions.DESACTIVATE_AUTOMATIC_BILLING
            )
          );
        }
        // if (
        //   isContractEligibleForEarlyTermination(
        //     contract,
        //     hasPendingCession,
        //     hasPendingTermination
        //   )
        // ) {
        //   actions.push(
        //     ContractActionMenuOptions.find(
        //       item =>
        //         item.key === ContractActionsOptions.TERMINATE_CONTRACT_EARLY
        //     )
        //   );
        // }
        if (!contract.automaticBilling) {
          actions.push(
            ContractActionMenuOptions.find(
              item =>
                item.key === ContractActionsOptions.ACTIVATE_AUTOMATIC_BILLING
            )
          );
        }
        if (
          isContractEligibleForCession(
            contract,
            hasPendingCession,
            hasPendingTermination
          )
        ) {
          actions.push(
            ContractActionMenuOptions.find(
              item => item.key === ContractActionsOptions.CEDE_CONTRACT
            )
          );
        }

        if (
          isContractEligibleForCancellation(
            contract,
            hasPendingCession,
            hasPendingTermination,
            role
          )
        ) {
          actions.push(
            ContractActionMenuOptions.find(
              item => item.key === ContractActionsOptions.CANCEL_CONTRACT
            )
          );
        }
        if (
          canGenerateTerminationLetter(contract.endDate) &&
          !contract?.terminationLetterDoc
        ) {
          actions.push(
            ContractActionMenuOptions.find(
              item =>
                item.key === ContractActionsOptions.GENERATE_TERMINATION_LETTER
            )
          );
        }
        if (isContractEligibleForTermination(contract)) {
          actions.push(
            ContractActionMenuOptions.find(
              item => item.key === ContractActionsOptions.TERMINATE_CONTRACT
            )
          );
        }
        if (hasPendingCession) {
          actions.push(
            ContractActionMenuOptions.find(
              item =>
                item.key ===
                ContractActionsOptions.RESEND_SIGN_REQUEST_FOR_CONTRACT_CESSION
            )
          );
          actions.push(
            ContractActionMenuOptions.find(
              item =>
                item.key === ContractActionsOptions.CANCEL_CONTRACT_CESSION
            )
          );
        }
        break;
      default:
        break;
    }

    if (!contract.automaticDebitRequest && contract.paymentForm === ContractPaymentForm.AUTOMATIC_DEBIT) {
      actions.push(
        ContractActionMenuOptions.find(
          item =>
            item.key ===
            ContractActionsOptions.GENERATE_AUTOMATIC_REQUEST_DOCUMENT
        )
      );
    } else if (contract.automaticDebitRequest && contract.paymentForm === ContractPaymentForm.AUTOMATIC_DEBIT) {
      actions.push(
        ContractActionMenuOptions.find(
          item =>
            item.key ===
            ContractActionsOptions.REGENERATE_AUTOMATIC_REQUEST_DOCUMENT
        )
      );
    }

    return actions;
  };

  const handleCloseDialog = useCallback(() => {
    setOpen('');
  });

  const handleOpenEditContract = useCallback(() => {
    setOpen(true);
  });

  const handleContractAction = action => {
    switch (action) {
      case ContractActionsOptions.SEND_TO_DIGITAL_SIGN:
        handleOpenSendToDigitalSign();
        break;
      case ContractActionsOptions.CANCEL_CONTRACT:
        handleOpenDeleteModal();
        break;
      case ContractActionsOptions.ACTIVATE_AUTOMATIC_BILLING:
        handleContractAutomaticBillingActivation();
        break;
      case ContractActionsOptions.DESACTIVATE_AUTOMATIC_BILLING:
        handleContractAutomaticBillingDeactivation();
        break;
      case ContractActionsOptions.EDIT_CONTRACT_DATES:
        handleOpenEditContract();
        break;
      case ContractActionsOptions.GENERATE_AUTOMATIC_REQUEST_DOCUMENT:
        handleOpenGenerateAutomaticDebitRequestDocDialog();
        break;
      case ContractActionsOptions.REGENERATE_AUTOMATIC_REQUEST_DOCUMENT:
        handleOpenRegenerateAutomaticDebitRequestDocDialog();
        break;
      case ContractActionsOptions.CEDE_CONTRACT:
        handleOpenCedeContractDialog();
        break;
      case ContractActionsOptions.TERMINATE_CONTRACT_EARLY:
        handleOpenEarlyTerminationContractDialog();
        break;
      case ContractActionsOptions.CANCEL_CONTRACT_CESSION:
        handleOpenCancelContractCessionDialog();
        break;
      case ContractActionsOptions.RESEND_SIGN_REQUEST_FOR_CONTRACT_CESSION:
        handleResendSignRequestForContractCession();
        break;
      case ContractActionsOptions.SEND_TO_DRAFT:
        handleOpenSendToDraftDialog();
        break;
      case ContractActionsOptions.GENERATE_TERMINATION_LETTER:
        handleOpenGenerateTerminationLetterDialog();
        break;
      case ContractActionsOptions.TERMINATE_CONTRACT:
        handleOpenTerminateContractDialog();
        break;
      default:
        break;
    }
  };

  const handleUpdateContractDates = useCallback(
    values => {
      dispatch(
        updateContractDates(values.id, values, () => handleCloseDialog())
      );
    },
    [dispatch, id]
  );

  return (
    <>
      <Grid
        item
        xs={12}
        sx={!openAlert && { display: 'none' }}
        marginBottom={3}
      >
        <AlertMessage
          open={openAlert}
          handleClose={() => {}}
          severity={AlertSeverity.WARNING}
          title="COMMON.WARNING"
          text="COMMON.CONTRACT_AUTOMATIC_BILLING_DEACTIVATED"
          hideCloseIcon
        />
      </Grid>

      <Grid container spacing={3} pb={4}>
        <Grid item xs={12} md={6} lg={3}>
          <Box
            className={classes.transaction}
            display="flex"
            alignItems="center"
          >
            <Box
              className={classes.contractStatusBox}
              sx={{
                backgroundColor: selectBackGroundColorFromContractStatus(
                  contract
                )
              }}
            >
              {getContractStatusIcon(contract)}
            </Box>
            <Box>
              <Typography
                style={{ marginBottom: 10 }}
                color="textPrimary"
                component="p"
                variant="body2"
              >
                {t('COMMON.FOLIO')}
              </Typography>
              <Typography color="primary" component="h2" variant="h2">
                {contract.folio}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Box
            className={classes.transaction}
            display="flex"
            alignItems="center"
          >
            <Box className={classes.transactionIcon}>
              <MoneyIcon fill="#28A745" />
            </Box>
            <Box>
              <Typography
                style={{ marginBottom: 10 }}
                color="textPrimary"
                component="p"
                variant="body2"
              >
                {contract.isInsuranceIncluded && contract.isMsiModality
                  ? t('COMMON.RENT_PLUS_INSURANCE')
                  : t('COMMON.RENT')}
              </Typography>
              <Typography color="primary" component="h2" variant="h2">
                {formatAmountWithDecimals(contract.rent)}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <PaymentMethodSelector contract={contract} />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            {contract.status !== ContractStatus.ENDED && (
              <MenuButton
                label={t('COMMON.CONTRACT_ACTIONS')}
                isLoading={isLoadingAction}
                disabled={
                  isLoadingAction ||
                  contract.status === ContractStatus.CANCELLED
                }
                options={selectContractActionsFromStatus()}
                handleClickOption={handleContractAction}
                styles={{ width: 300 }}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      <EditContract
        contract={contract}
        open={open}
        handleClose={handleCloseDialog}
        submit={handleUpdateContractDates}
      />

      <DeleteConfirmationDialog
        open={openCancelContract}
        handleClose={handleCloseDeleteModal}
        handleSubmit={handleCancelContract}
        contractStatus={contract.status}
      />

      <ConfirmationDialog
        open={openSendToDigitalSign}
        handleClose={handleCloseSendToDigitalSign}
        onSubmit={handleSendToSign}
        title={t('CONTRACT_VIEW.CONTRACT_DETAIL.SEND_TO_DIGITAL_SIGN')}
        description={t(
          'CONTRACT_VIEW.CONTRACT_DETAIL.SEND_TO_DIGITAL_SIGN_DESCRIPTION'
        )}
      />

      <ConfirmationDialog
        open={openGenerateAutomaticRequestDoc}
        handleClose={handleCloseGenerateAutomaticDebitRequestDocDialog}
        onSubmit={handleGenerateAutomaticDebitRequestDocument}
        title={t('CONTRACT_VIEW.GENERATE_AUTOMATIC_REQUEST_DOC_TITLE')}
        description={t(
          'CONTRACT_VIEW.GENERATE_AUTOMATIC_REQUEST_DOC_DESCRIPTION'
        )}
      />

      <ConfirmationDialog
        open={openRegenerateAutomaticRequestDoc}
        handleClose={handleCloseRegenerateAutomaticDebitRequestDocDialog}
        onSubmit={handleRegenerateAutomaticDebitRequestDocument}
        title={t('CONTRACT_VIEW.REGENERATE_AUTOMATIC_REQUEST_DOC_TITLE')}
        description={t(
          'CONTRACT_VIEW.REGENERATE_AUTOMATIC_REQUEST_DOC_DESCRIPTION'
        )}
      />

      <ConfirmationDialog
        open={openCancelContractCession}
        handleClose={handleCloseCancelContractCessionDialog}
        onSubmit={handleCancelContractCession}
        title={t('CONTRACT_VIEW.CONTRACT_DETAIL.CANCEL_CONTRACT_CESSION')}
        description={t(
          'CONTRACT_VIEW.CONTRACT_DETAIL.CANCEL_CONTRACT_CESSION_DIALOG_DESCRIPTION'
        )}
      />

      <ConfirmationDialog
        open={openGenerateTerminationLetter}
        handleClose={handleCloseGenerateTerminationLetterDialog}
        onSubmit={handleGenerateTerminationLetter}
        title={t('CONTRACT_VIEW.CONTRACT_DETAIL.GENERATE_TERMINATION_LETTER')}
      >
        <AlertMessage
          open={clientBalance > 0}
          severity={AlertSeverity.WARNING}
          text={t('COMMON.SEND_TERMINATION_LETTER_TO_CLIENT_WARNING', {
            balance: formatAmountWithDecimals(clientBalance, 2)
          })}
          hideCloseIcon
        />
        <Typography
          sx={theme => ({
            color: theme.palette.text.primary,
            marginTop: 2,
            textAlign: 'justify',
            fontSize: theme.spacing(2.5)
          })}
        >
          {t(
            'CONTRACT_VIEW.CONTRACT_DETAIL.GENERATE_TERMINATION_LETTER_DESCRIPTION'
          )}
        </Typography>
      </ConfirmationDialog>

      <CedeContractForm
        open={openCedeContract}
        contract={contract}
        handleClose={handleCloseCedeContractDialog}
        onSubmit={handleCedeContract}
        title={t('CONTRACT_VIEW.CEDE_CONTRACT_FORM_TITLE')}
        description={t(
          'CONTRACT_VIEW.CONTRACT_DETAIL.CEDE_CONTRACT_DESCRIPTION'
        )}
      />
      <EarlyTerminationContractForm
        open={openEarlyTerminationContract}
        contract={contract}
        handleClose={handleCloseEarlyTerminationContractDialog}
        onSubmit={handleEarlyTerminationContract}
        title={t('CONTRACT_VIEW.EARLY_TERMINATION_CONTRACT_FORM')}
        description={t(
          'CONTRACT_VIEW.CONTRACT_DETAIL.EARLY_TERMINATION_CONTRACT_DESCRIPTION'
        )}
      />

      <ConfirmationDialog
        open={openSendToDraft}
        handleClose={handleCloseSendToDraftDialog}
        onSubmit={handleSendToDraft}
        title={t('CONTRACT_VIEW.CONTRACT_DETAIL.SEND_TO_DRAFT')}
        description={t(
          'CONTRACT_VIEW.CONTRACT_DETAIL.SEND_TO_DRAFT_DESCRIPTION'
        )}
      />

      <ConfirmationDialog
        open={openTerminateContract}
        handleClose={handleCloseTerminateContractDialog}
        onSubmit={handleTerminateContract}
        title={t('CONTRACT_VIEW.CONTRACT_DETAIL.TERMINATE_CONTRACT')}
        isLoading={isLoadingTerminateContract}
      >
        <AlertMessage
          open={clientBalance > 0}
          severity={AlertSeverity.WARNING}
          text={t('COMMON.TERMINATE_CONTRACT_WARNING', {
            balance: formatAmountWithDecimals(clientBalance, 2)
          })}
          hideCloseIcon
        />
        <Typography
          sx={theme => ({
            color: theme.palette.text.primary,
            marginTop: 2,
            textAlign: 'justify',
            fontSize: theme.spacing(2.5)
          })}
        >
          {t('CONTRACT_VIEW.CONTRACT_DETAIL.TERMINATE_CONTRACT_DESCRIPTION')}
        </Typography>
      </ConfirmationDialog>
    </>
  );
};

export default DetailHeader;

DetailHeader.propTypes = {
  contract: PropTypes.object.isRequired
};
